// React imports
import { useState } from 'react';
import PropTypes from 'prop-types';

//  material-ui imports
import { Stack, Typography } from '@mui/material';

// project imports
import FileMngrDialog from './FileMngrDialog';

// third-party imports
import { useIntl } from 'react-intl';
import { useDispatch } from 'store';
import { openSnackbar } from 'store/slices/snackbar';

// ==============================|| FILEMNGR DIALOG DELETE CONFIRMATION ||============================== //

/**
 * FileMngrDialogDeleteConfirmation component - Responsible for renaming folders and files in the file manager dialog.
 * @author Marcos L. A. Soto <marcosleonildo@gmail.com>
 * @param {Object} props - The component props.
 * @param {boolean} props.openRenameDialog - Flag to control the visibility of the confirmation dialog dialog.
 * @param {Function} props.handleCloseRenameDialog - Callback function to handle the close event of the confirmation dialog dialog.
 * @param {Function} props.handleRefreshFolder - Callback function to handle the refresh event of the folder.
 * @returns {JSX.Element} The JSX element representing the delete confirmation dialog component.
 */
const FileMngrDialogDeleteConfirmation = ({ deleteDialogOpen, handleDeleteDialogClose, deleteItem }) => {
    const [isLoading, setIsLoading] = useState(false);

    const intl = useIntl();
    const dispatch = useDispatch();

    const handleDeleteItem = async () => {
        setIsLoading(true);
        try {
            await deleteItem();
            handleDeleteDialogClose();
        } catch (error) {
            dispatch(
                openSnackbar({
                    open: true,
                    message: error?.message || intl.formatMessage({ id: 'ui-comp-snackbar-genericerror-text' }),
                    variant: 'alert',
                    alert: { color: 'error' }
                })
            );
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <FileMngrDialog
            title={intl.formatMessage({ id: 'ui-comp-filemngr-dialog-deleteconfirmation-title' })}
            isOpen={deleteDialogOpen}
            onClose={handleDeleteDialogClose}
            buttons={[
                {
                    text: intl.formatMessage({ id: 'ui-comp-filemngr-dialog-deleteconfirmation-button-text' }),
                    onClick: handleDeleteItem,
                    variant: 'contained',
                    color: 'warning',
                    disabled: isLoading,
                    isLoading
                }
            ]}
        >
            <Stack spacing={1.5} justifyContent="center" alignItems="center" width={320} p={1}>
                <Typography variant="subtitle1">
                    {intl.formatMessage({ id: 'ui-comp-filemngr-dialog-deleteconfirmation-questtext' })}
                </Typography>
            </Stack>
        </FileMngrDialog>
    );
};

FileMngrDialogDeleteConfirmation.propTypes = {
    deleteDialogOpen: PropTypes.bool,
    handleDeleteDialogClose: PropTypes.func,
    deleteItem: PropTypes.func
};

export default FileMngrDialogDeleteConfirmation;
